// Where Code › Apps, an app's page, the open hand-off and the old
// /agent-dev addresses land (apps spec §10.1, §10.3, §8.3, §2.2 item 10).
//
// The Apps pages themselves are stream F1's files (pages/AppOpenPage.tsx,
// pages/customers/AppsTab.tsx, features/apps/{AppsPane,AppPage,AppNeedsRows}.tsx;
// the admin's Apps switch is the Features table's since 30 Oct). They are loaded lazily by a literal
// import.meta.glob, which finds nothing while a file is not in the build:
// each export below is then null and its route falls back to today's
// screen, so this file builds with or without F1's and the night's merge
// needs no edit here.
//
//   /code/apps          Apps on: F1's AppsTab (else AppsPane); off: today's list (CCDeploysTab)
//   /code/apps/:slug    Apps on: the app page; off or absent: back to /code/apps
//   /agent-dev/*        "No app here." (never the dashboard); /agent-dev/open/app is App.tsx's

import { Suspense, lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";
import { Link, Navigate, Route, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { useAppsFlag } from "@/api/appsSummary";
import { PageContainer } from "@/components/layout/PageContainer";
import { appsNeedYouLabel } from "@/features/attention/predicates";
import { useAuth } from "@/stores/auth";

import { CCDeploysTab, RouteLoading, page } from "./pages";

type Mod = Record<string, unknown>;
type Loader = () => Promise<Mod>;

/** A lazy component from a module that may not be in this build (null when absent). */
function optional<P extends object>(found: Record<string, Loader>, name: string): LazyExoticComponent<ComponentType<P>> | null {
  const load = Object.values(found)[0];
  if (!load) return null;
  return lazy(async () => {
    const mod = await load();
    return { default: mod[name] as ComponentType<P> };
  });
}

export const AppOpenPage = optional<object>(import.meta.glob<Mod>("../pages/AppOpenPage.tsx"), "AppOpenPage");
export const AppsTab = optional<object>(import.meta.glob<Mod>("../pages/customers/AppsTab.tsx"), "AppsTab");
export const AppsPane = optional<{ customerId: string }>(import.meta.glob<Mod>("../features/apps/AppsPane.tsx"), "AppsPane");
export const AppPage = optional<{ customerId: string; slug: string }>(import.meta.glob<Mod>("../features/apps/AppPage.tsx"), "AppPage");
export const AppNeedsRows = optional<{ customerId: string }>(import.meta.glob<Mod>("../features/apps/AppNeedsRows.tsx"), "AppNeedsRows");

/** The page frame of Code › Apps and an app's page: the page gutter (16 px on a phone) and the top inset. */
const FRAME = "pb-10 pt-5 md:pb-10 md:pt-5";

/** Code › Apps: the Apps list where Apps is on, today's list where it is off. */
export function AppsRoute() {
  const customerId = useAuth((s) => s.customerId) ?? "";
  const on = useAppsFlag(customerId);
  if (on === undefined) return <RouteLoading />;
  if (on && AppsTab) return <AppsTab />;
  if (on && AppsPane) {
    return (
      <PageContainer className={FRAME}>
        <AppsPane customerId={customerId} />
      </PageContainer>
    );
  }
  return <CCDeploysTab />;
}

/** One app's page (its own page, outside the Code tab strip), in the same frame as every other page. */
export function AppPageRoute({ back = "/code/apps" }: { back?: string }) {
  const customerId = useAuth((s) => s.customerId) ?? "";
  const { slug = "" } = useParams();
  const on = useAppsFlag(customerId);
  if (on === undefined) return <RouteLoading />;
  if (!on || !AppPage || !slug) return <Navigate to={back} replace />;
  return (
    <PageContainer className={FRAME}>
      <AppPage customerId={customerId} slug={slug} />
    </PageContainer>
  );
}

/** /agent-dev/<anything>: never the dashboard (the edge sends one-segment paths elsewhere). */
export function NoAppHere() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-950 px-4 text-center">
      <h1 className="text-lg font-semibold text-fg">No app here.</h1>
      <Link to="/" className="rounded-control text-sm text-accent-fg hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus">
        Back to Lattice
      </Link>
    </main>
  );
}

/**
 * /agent-dev/<anything> that reaches the web (the edge sends one-segment
 * paths to /cc-deploys): "No app here.", never the dashboard, on every
 * width, outside any shell, for every role (apps spec §2.2 item 10).
 * /agent-dev/open/app is answered before this, in App.tsx.
 */
export function agentDevRoutes() {
  return <Route path="agent-dev/*" element={<NoAppHere />} />;
}

/** /agent-dev/open/app, signed in: F1's hand-off page, else "No app here." (a P0 build without it). */
export function AppOpenRoute(): ReactNode {
  return AppOpenPage ? page(<AppOpenPage />) : <NoAppHere />;
}

/**
 * The phone's Today › Needs you (rows of a <ul>): F1's rows of apps that
 * wait, one per app with No · Allow or Restart in place; without them in
 * this build, one row to Apps that says how many wait.
 */
export function AppNeedsRowsSlot({ customerId, waiting }: { customerId: string; waiting: number }) {
  const on = useAppsFlag(customerId) === true;
  if (!on || waiting <= 0) return null;
  if (!AppNeedsRows) {
    return (
      <li className="border-b border-line-subtle last:border-b-0">
        <Link to="/companion/apps" className="flex min-h-[52px] w-full items-center gap-3 py-2.5">
          <span className="min-w-0 flex-1 truncate text-base text-fg">Apps · {appsNeedYouLabel(waiting)}</span>
          <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-fg-subtle" />
        </Link>
      </li>
    );
  }
  return (
    <Suspense fallback={null}>
      <AppNeedsRows customerId={customerId} />
    </Suspense>
  );
}
